import { Theme as AugmentedTheme } from '@mui/material/styles';


import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { Box, Button, MUIDataTable, MUIDataTableColumnDef, Pill } from 'web-library';
import { KeyValueResponse } from '../../../api';
import FormHeader from '../../../components/form-items/FormHeader';
import EligibleSchoolOptions from './EligibleSchoolOptions';
import GrantAccessModal from './GrantAccessModal';
import { useUniqueLink } from './logic/useUniqueLink';
import { GenericExamLevel } from '../../../api/candidate/exam/model';
import {
  CoordinatorUniqueLinkCountResponse,
  useGetCoordinatorUniqueLinkCount,
} from '../../../api-refactored/unique-link/useGetCoordinatorUniqueLinkCount';
import UniqueLinkListingModal from './UniqueLinkListingModal';

const useStyles = makeStyles()((theme) =>
({
  tableContainer: {
    marginTop: theme.spacing(3),
  },
  contentContainer: {
    marginTop: theme.spacing(6),
  },
  viewDetailsButton: {
    padding: 0,
    minHeight: 0,
    minWidth: 0,
    whiteSpace: 'nowrap',
    height: theme.spacing(3),
  },
}));

const allowedExamLevelWithMultipleUniqueLinks = [GenericExamLevel.GCE, GenericExamLevel.PSLE];

const displayManageUniqueLinkButton = (
  coordinatorUuid: string,
  uniqueLinkCount: number,
  t: TFunction,
  classes: Record<string,string>,
  setSelectedCoordinatorUuid: React.Dispatch<React.SetStateAction<string | undefined>>,
  selectedExamLevel: GenericExamLevel | undefined
) =>
  uniqueLinkCount > 0 &&
  allowedExamLevelWithMultipleUniqueLinks.some((e) => e === selectedExamLevel) && (
    <>
      <Button
        className={classes.viewDetailsButton}
        color="primary"
        variant="text"
        size="small"
        onClick={() => setSelectedCoordinatorUuid(coordinatorUuid)}
      >
        {t('admin->>UniqueLink.ManageUniqueLink')}
      </Button>
    </>
  );

/** define Mui DataTable Column Definition
 * */
const tableColumns = (
  classes: any,
  t: TFunction,
  coordinatorList: CoordinatorUniqueLinkCountResponse[],
  setEditDetail: (name: string, uuid: string, index: number) => void,
  sendEmailStatusList: string[],
  hasCreatePermission: boolean,
  setSelectedCoordinatorUuid: React.Dispatch<React.SetStateAction<string | undefined>>,
  selectedExamLevel: GenericExamLevel | undefined
): MUIDataTableColumnDef[] => [
    {
      name: 'coordinatorName',
      label: t('admin->>UniqueLink.CoordinatorName'),
      options: {
        searchable: true,
        sort: false,
        customBodyRenderLite: (dataIndex: number) => coordinatorList[dataIndex].coordinator.coordinatorName,
      },
    },
    {
      name: 'email',
      label: t('admin->>UniqueLink.Email'),
      options: {
        searchable: true,
        sort: false,
        customBodyRenderLite: (dataIndex: number) => coordinatorList[dataIndex].coordinator.email,
      },
    },
    {
      name: 'school',
      label: t('admin->>UniqueLink.School'),
      options: {
        sort: false,
        searchable: true,
        customBodyRenderLite: (dataIndex: number) => coordinatorList[dataIndex].coordinator.school,
      },
    },
    {
      name: 'action',
      label: t('admin->>UniqueLink.Action'),
      options: {
        customBodyRenderLite: (dataIndex: number) =>
          !sendEmailStatusList[dataIndex] || sendEmailStatusList[dataIndex] !== 'success' ? (
            <>
              <Button
                className={classes.viewDetailsButton}
                color="primary"
                variant="text"
                size="small"
                onClick={() => {
                  setEditDetail(
                    coordinatorList[dataIndex].coordinator.coordinatorName,
                    coordinatorList[dataIndex].coordinator.uuid,
                    dataIndex
                  );
                }}
              >
                {t('admin->>UniqueLink.GrantAccess')}
              </Button>
              {displayManageUniqueLinkButton(
                coordinatorList[dataIndex].coordinator.uuid,
                coordinatorList[dataIndex].uniqueLinkCount,
                t,
                classes,
                setSelectedCoordinatorUuid,
                selectedExamLevel
              )}
            </>
          ) : (
            <>
              <Pill label={t('admin->>UniqueLink.EmailSent')} size="S" variant="positive" />
              {displayManageUniqueLinkButton(
                coordinatorList[dataIndex].coordinator.uuid,
                coordinatorList[dataIndex].uniqueLinkCount,
                t,
                classes,
                setSelectedCoordinatorUuid,
                selectedExamLevel
              )}
            </>
          ),
        sort: false,
        filter: false,
        display: `${hasCreatePermission}`,
      },
    },
  ];

const CoordinatorGroupExamRegistrationList = ({
  selectedExamType,
  setExamLevelError,
  nationalExamType,
  hasCreatePermission,
  isGCESelected,
  selectedExamLevel,
}: {
  selectedExamType?: string;
  setExamLevelError: (examLevelError: boolean) => void;
  nationalExamType: string;
  hasCreatePermission: boolean;
  isGCESelected: boolean;
  selectedExamLevel?: GenericExamLevel;
}) => {
  const [selectedOrganization, setSelectedOrganization] = useState<KeyValueResponse>();
  const [selectedCoordinatorUuid, setSelectedCoordinatorUuid] = useState<string>();

  const {
    grantAccessModalOpen,
    selectedUserInfo,
    sendEmailStatusList,
    setSentEmailStatusList,
    apiEmailStatusCallback,
    onGrantAccessDialogClose,
    setEditDetail,
  } = useUniqueLink();

  const { t } = useTranslation();
  const { classes } = useStyles();

  const {
    data: coordinatorList,
    isLoading,
    refetch,
  } = useGetCoordinatorUniqueLinkCount({
    orgUuid: selectedOrganization?.code,
    examLevel: selectedExamLevel,
  });

  useEffect(() => {
    if (selectedOrganization) {
      setSentEmailStatusList([]);
      refetch().then();
    }
  }, [refetch, selectedOrganization, setSentEmailStatusList]);

  return (
    <>
      <FormHeader title={t('admin->>UniqueLink.CoordinatorDetails')} />

      <Box className={classes.contentContainer}>
        <EligibleSchoolOptions
          key={`school-${nationalExamType}-${selectedExamType}`}
          value={selectedOrganization}
          onChange={(value) => {
            if (!selectedExamType) {
              setExamLevelError(true);
              return;
            }
            setSelectedOrganization(value);
          }}
          disabled={!selectedExamType}
        />
      </Box>

      <div className={classes.tableContainer}>
        <MUIDataTable
          key="private-center"
          isLoading={isLoading}
          columns={tableColumns(
            classes,
            t,
            coordinatorList || [],
            setEditDetail,
            sendEmailStatusList,
            hasCreatePermission,
            setSelectedCoordinatorUuid,
            selectedExamLevel
          )}
          data={coordinatorList || []}
        />
      </div>

      {grantAccessModalOpen && selectedUserInfo && selectedExamType && (
        <GrantAccessModal
          accountType="COORDINATORS"
          examLevelUuid={selectedExamType}
          userInfo={selectedUserInfo}
          modalState={grantAccessModalOpen}
          onDialogClose={onGrantAccessDialogClose}
          updateEmailStatus={apiEmailStatusCallback}
          showWavieSubjectFee={nationalExamType === 'national'}
          applicationType={nationalExamType === 'national' ? 'EXAM' : 'NON_NATIONAL_EXAM'}
          isGCESelected={isGCESelected}
          refetchCoordinatorListing={refetch}
        />
      )}

      <UniqueLinkListingModal
        selectedCoordinatorUuid={selectedCoordinatorUuid}
        setSelectedCoordinatorUuid={setSelectedCoordinatorUuid}
        examLevel={selectedExamLevel}
        userType={'Coordinator'}
      />
    </>
  );
};
export default CoordinatorGroupExamRegistrationList;
